import React from "react";
import { TbArrowLeft, TbShieldHalf, TbSearch, TbPresentation, TbPlus, TbTrash, TbArrowUp, TbArrowDown, TbLock,
         TbChevronRight, TbLoader2 } from "react-icons/tb";
import { useAssessment } from "../../hooks/useAssessment";
import { Button, Alert, EmptyState, TopbarSlot, Modal, Field } from "../../components";
import { CrumbSlot, NavSlot } from "../../shell/slots";
import { RATING_LABEL, SOURCE_LABEL, HEALTH_LABEL, healthFor } from "../../lib/ratings";
import RatingPicker from "./RatingPicker";
import ScoreRing from "./ScoreRing";
import ItemPanel from "./ItemPanel";
import ExportMenu, { EXPORT_FORMATS } from "./ExportMenu";
import { useDeliverable } from "./useDeliverable";
import SwotPanel from "./SwotPanel";
import ComparisonPanel from "./ComparisonPanel";
import PresentationView from "./PresentationView";
import StatusControl from "./StatusControl";
import AddItemsModal from "./AddItemsModal";
import "./Reviews.css";

// The assessment editor. One category at a time, because a full review is thirty-odd
// items and the kit shows them split by category rather than as one long list.
const AssessmentEditorScreen = ({ id, me, onBack, siblings = [] }) => {
  const {
    assessment, categories, items, score, counts, loading, error, saving,
    rateItem, update, addCategory, removeCategory, addItems, removeItem, reorderItems,
  } = useAssessment(id);
  const [activeCategory, setActiveCategory] = React.useState(null);
  // The kit's editor nav has a Review group below the categories: the SWOT and the
  // comparison are the same assessment seen two other ways, not separate screens.
  const [pane, setPane] = React.useState("items");
  const [find, setFind] = React.useState("");
  const [presenting, setPresenting] = React.useState(false);
  const [adding, setAdding] = React.useState(null);
  const [namingCategory, setNamingCategory] = React.useState(false);
  const [openItemId, setOpenItemId] = React.useState(null);
  // The client report and its export, owned here so Export works from every tab of the
  // editor, not just from inside Preview.
  const deliverable = useDeliverable({ assessment, categories, items, counts, score, me, siblings });
  const { exported, exportError, clearExportStatus } = deliverable;
  // A finished download says so, briefly; an error stays until it is dismissed or retried.
  React.useEffect(() => {
    if (!exported) return undefined;
    const t = setTimeout(clearExportStatus, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exported]);

  if (loading) return <div className="v2-page"><p className="v2-hint">Loading…</p></div>;
  if (!assessment) {
    return (
      <div className="v2-page">
        <Alert>{error || "That assessment could not be loaded."}</Alert>
        <Button onClick={onBack}><TbArrowLeft size={15} /> Back to assessments</Button>
      </div>
    );
  }

  const currentId = activeCategory ?? categories?.[0]?.id;
  const current = (categories || []).find((c) => c.id === currentId);
  // "Find an item" searches the whole assessment, not just the open category — you are
  // looking for an item, and you do not necessarily know which category it is in.
  const q = find.trim().toLowerCase();
  const shown = q
    ? (items || []).filter((i) => (i.name || "").toLowerCase().includes(q))
    : (items || []).filter((i) => i.category_id === currentId);
  const health = assessment.health || healthFor(score);
  // A presented review is a record of what a client was shown, so its contents are
  // read-only. The server enforces this too; the UI just stops offering the controls.
  const locked = assessment.state === "presented";

  // Reorder writes the whole ordered list for the category rather than swapping a pair,
  // because a partial write is how two items end up sharing a position.
  const move = (index, delta) => {
    const ids = shown.map((i) => i.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderItems(currentId, ids);
  };
  const usedLibraryIds = (items || []).map((i) => i.library_item_id).filter(Boolean);

  return (
    // data-tour-page: the editor renders inside the Assessments screen, so it names its
    // own How it works tour rather than getting the assessment list's (shell/tours.js).
    <div className="v2-page" data-tour-page="assessment-editor">
      <CrumbSlot>
        <div className="v2-crumbs">
          <button type="button" className="v2-crumb-link" onClick={onBack}>Business reviews</button>
          <span aria-hidden="true">/</span>
          <span>{assessment.organization_name}</span>
          <span aria-hidden="true">/</span>
          <b>{assessment.title}</b>
          {/* The autosave state, where the design puts it. Silence here would leave
              someone wondering whether their last rating stuck. */}
          <span className={`v2-savestate${saving ? " on" : ""}`}>
            <span className="v2-savestate-dot" />
            {saving ? "Saving…" : "Saved"}
          </span>
        </div>
      </CrumbSlot>

      <TopbarSlot>
        <div className="v2-omni">
          <TbSearch size={14} />
          <input type="search" value={find} placeholder="Find an item" aria-label="Find an item"
                 onChange={(e) => setFind(e.target.value)} />
        </div>
        {/* A toggle, not a one-way trip. It used to only ever set the pane to
            "presentation" — which is where it leaves you — so pressing it a second time
            did nothing at all, and it was indistinguishable from the Presentation item in
            the nav. Now it flips back to the items you were rating, which is the thing
            you actually want after looking at the preview. */}
        <Button
          className={pane === "presentation" ? "on" : ""}
          data-tour="editor-preview"
          aria-pressed={pane === "presentation"}
          onClick={() => setPane((p) => (p === "presentation" ? "items" : "presentation"))}
        >
          {pane === "presentation" ? "Back to items" : "Preview"}
        </Button>
        <StatusControl value={assessment.state} compact
                       onChange={(state) => update({ state })} />
        <ExportMenu exporting={deliverable.exporting} onExport={deliverable.exportAs} />
        <Button variant="primary" data-tour="editor-present" onClick={() => setPresenting(true)}>
          <TbPresentation size={15} /> Present
        </Button>
      </TopbarSlot>

      <NavSlot>
        {/* The way out, first. This nav has replaced the Business reviews menu, so
            without it the only route back is the breadcrumb. */}
        <button type="button" className="v2-nav-item v2-nav-back" onClick={onBack}>
          <TbArrowLeft size={15} />
          <span>All assessments</span>
        </button>

        {/* The organization, not the workspace: while you are in an assessment, the
            client is the context. */}
        <div className="v2-tenant">
          <span className="v2-tenant-mark">{(assessment.organization_name || "?").slice(0, 1).toUpperCase()}</span>
          <span className="v2-tenant-name">{assessment.organization_name}</span>
        </div>

        <div className="v2-nav-label">Categories</div>
        {(categories || []).map((c) => (
          <button key={c.id} type="button"
                  className={`v2-nav-item${pane === "items" && c.id === currentId ? " on" : ""}`}
                  aria-current={pane === "items" && c.id === currentId ? "page" : undefined}
                  onClick={() => { setPane("items"); setActiveCategory(c.id); }}>
            <span>{c.name}</span>
            <span className="v2-nav-count">{c.rated}/{c.total}</span>
          </button>
        ))}

        {!locked && (
          <button type="button" className="v2-nav-item v2-nav-add" onClick={() => setNamingCategory(true)}>
            <TbPlus size={15} />
            <span>Add category</span>
          </button>
        )}

        <div className="v2-nav-label">Review</div>
        <button type="button" className={`v2-nav-item${pane === "swot" ? " on" : ""}`}
                onClick={() => setPane("swot")}>
          <span>SWOT and action plan</span>
        </button>
        <button type="button" className={`v2-nav-item${pane === "presentation" ? " on" : ""}`}
                onClick={() => setPane("presentation")}>
          <span>Presentation</span>
        </button>
        <button type="button" className={`v2-nav-item${pane === "compare" ? " on" : ""}`}
                onClick={() => setPane("compare")}>
          <span>Compare</span>
        </button>

        {/* Export, as its own group: the files the client receives are the end product of
            this screen, and they should be as easy to find as the categories. */}
        <div className="v2-nav-label">Export</div>
        {EXPORT_FORMATS.map(({ kind, label, icon: Icon }) => (
          <button key={kind} type="button" className="v2-nav-item v2-nav-export"
                  disabled={!!deliverable.exporting} onClick={() => deliverable.exportAs(kind)}>
            {deliverable.exporting === kind ? <TbLoader2 size={15} className="v2-spin" /> : <Icon size={15} />}
            <span>{deliverable.exporting === kind ? "Creating…" : `Download ${label}`}</span>
          </button>
        ))}


      </NavSlot>

      <Alert>{error || exportError}</Alert>
      {exported && (
        <Alert tone="success">
          {exported === "pdf" ? "The PDF report has downloaded." : "The PowerPoint deck has downloaded."}
        </Alert>
      )}

      {locked && (
        <div className="v2-lockbar">
          <TbLock size={16} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>This review has been presented</b>
            <span>Its contents are locked so the record of what the client saw stays intact. Clone it to carry on.</span>
          </div>
          <Button onClick={() => update({ state: "ready" })}>Move back to Ready</Button>
        </div>
      )}

      <div className="v2-scoreband">
        <ScoreRing score={score} />
        <div>
          <div className="v2-scoreband-value">
            {score === null || score === undefined ? "Not yet scored" : "Current score"}
          </div>
          <div className="v2-scoreband-label">
            {(items || []).filter((i) => i.rating !== "unrated" && i.rating !== "na").length} of{" "}
            {(items || []).filter((i) => i.rating !== "na").length} items rated, weighted
          </div>
          <div className={`v2-health v2-health--${health}`} style={{ marginTop: ".25rem" }}>
            {HEALTH_LABEL[health]}
          </div>
        </div>
        <div className="v2-pills">
          {["green", "yellow", "red", "unrated"].map((r) => (
            <span key={r} className={`v2-rpill v2-rpill--${r}`}>
              {counts?.[r] ?? 0} {r === "unrated" ? "unrated" : r}
            </span>
          ))}
        </div>
      </div>

      {assessment.linked_scan_id ? (
        <div className="v2-scanlink" style={{ marginTop: ".875rem" }}>
          <span className="v2-scanlink-ic"><TbShieldHalf size={15} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>Items pre-rated from this organization's external risk scan</b>
            <p>Every pre-rating is editable, and carries the finding as evidence. Changing one
               marks the item as consultant rated.</p>
          </div>
        </div>
      ) : null}

      {/* The categories live in the context nav now, as the design has them. These tabs
          stay because the nav is a jump list and these are where you are — and the nav
          disappears entirely below 900px, where they become the only way across. */}
      <div className="v2-views" style={{ marginTop: ".875rem" }}>
        {(categories || []).map((c) => (
          <button key={c.id} type="button"
                  className={`v2-view${pane === "items" && c.id === currentId ? " on" : ""}`}
                  onClick={() => { setPane("items"); setActiveCategory(c.id); }}>
            {c.name}<span className="n">{c.total}</span>
          </button>
        ))}
        <span className="v2-views-sep" aria-hidden="true" />
        <button type="button" className={`v2-view${pane === "swot" ? " on" : ""}`}
                onClick={() => setPane("swot")}>SWOT and action plan</button>
        <button type="button" className={`v2-view${pane === "compare" ? " on" : ""}`}
                onClick={() => setPane("compare")}>Compare</button>
      </div>

      {pane === "presentation" ? (
          <div style={{ marginTop: ".875rem" }}>
            <PresentationView assessment={assessment} score={score} counts={counts} me={me}
                              deliverable={deliverable} />
          </div>
        )
        : pane === "swot" ? <div style={{ marginTop: ".875rem" }}><SwotPanel assessmentId={id} /></div>
        : pane === "compare" ? (
          <div style={{ marginTop: ".875rem" }}>
            <ComparisonPanel assessment={assessment} siblings={siblings} />
          </div>
        )
        : shown.length === 0 ? (
        <EmptyState title="Nothing in this category">This category has no items.</EmptyState>
      ) : (
        <div className="v2-tablewrap" style={{ marginTop: ".875rem" }}>
          <table className="v2-table">
            <thead>
              <tr>
                <th style={{ width: "34%" }}>Item</th>
                <th style={{ width: "190px" }}>Rating</th>
                <th>Weight</th>
                <th>Source</th>
                <th>Statement</th>
                {!locked && <th className="v2-num">Edit</th>}
              </tr>
            </thead>
            <tbody>
              {shown.map((item, index) => (
                <React.Fragment key={item.id}>
                <tr className={openItemId === item.id ? "v2-row-open" : undefined}>
                  <td>
                    {/* Opens the write-up in place, under this row. */}
                    <button type="button" className="v2-linkcell v2-itemlink"
                            aria-expanded={openItemId === item.id}
                            onClick={() => setOpenItemId(openItemId === item.id ? null : item.id)}>
                      <TbChevronRight size={14} className="v2-itemlink-chev" aria-hidden="true" />
                      {item.name}
                    </button>
                  </td>
                  <td>
                    <RatingPicker
                      value={item.rating}
                      itemName={item.name}
                      disabled={locked}
                      onChange={(rating) => rateItem(item.id, { rating })}
                    />
                  </td>
                  <td className="v2-num">{item.rating === "na" ? "." : Number(item.weight).toFixed(1)}</td>
                  <td>{SOURCE_LABEL[item.source] || item.source}</td>
                  <td>{item.summary || RATING_LABEL[item.rating]}</td>
                  {!locked && (
                    <td className="v2-num">
                      <span className="v2-rowtools">
                        <Button icon aria-label={`Move ${item.name} up`} disabled={index === 0}
                                onClick={() => move(index, -1)}><TbArrowUp size={14} /></Button>
                        <Button icon aria-label={`Move ${item.name} down`} disabled={index === shown.length - 1}
                                onClick={() => move(index, 1)}><TbArrowDown size={14} /></Button>
                        <Button icon variant="danger" aria-label={`Remove ${item.name}`}
                                onClick={() => removeItem(item.id)}><TbTrash size={14} /></Button>
                      </span>
                    </td>
                  )}
                </tr>
                {openItemId === item.id && (
                  <tr className="v2-ipanel-row">
                    <td colSpan={locked ? 5 : 6}>
                      <ItemPanel
                        item={item}
                        categoryName={(categories || []).find((c) => c.id === item.category_id)?.name}
                        position={index + 1}
                        total={shown.length}
                        locked={locked}
                        onSave={rateItem}
                        onPrev={index > 0 ? () => setOpenItemId(shown[index - 1].id) : null}
                        onNext={index < shown.length - 1 ? () => setOpenItemId(shown[index + 1].id) : null}
                        onClose={() => setOpenItemId(null)}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pane === "items" && !locked && current && (
        <div className="v2-itemtools">
          <Button onClick={() => setAdding(current)}>
            <TbPlus size={15} /> Add items to {current.name}
          </Button>
          <Button variant="danger" onClick={() => {
            if (window.confirm(`Remove the ${current.name} category and its ${shown.length} items from this review?`)) {
              removeCategory(current.id);
              setActiveCategory(null);
            }
          }}>
            Remove category
          </Button>
        </div>
      )}

      {pane === "items" && (
      <p className="v2-editor-foot">
        Items marked out of scope leave the score entirely. Unrated items still count
        against it, so a half finished review reads lower than a finished one — but an
        assessment with nothing rated yet reads as unscored, not as a failing one.
      </p>
      )}

      {presenting && (
        <PresentationView assessment={assessment} score={score} counts={counts} me={me}
                          deliverable={deliverable} onClose={() => setPresenting(false)} />
      )}

      {namingCategory && (
        <NameCategoryModal
          onClose={() => setNamingCategory(false)}
          onCreate={async (name) => addCategory(name)}
        />
      )}

      {adding && (
        <AddItemsModal
          category={adding}
          usedLibraryIds={usedLibraryIds}
          onClose={() => setAdding(null)}
          onAdd={addItems}
        />
      )}

    </div>
  );
};

// A dialog rather than window.prompt, which is the only place in v2 that used one and
// looked like a different application when it appeared.
const NameCategoryModal = ({ onClose, onCreate }) => {
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const ok = await onCreate(name.trim());
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Modal title="Add a category" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy || !name.trim()}>
          {busy ? "Adding…" : "Add category"}
        </Button>
      </>}>
      <Field label="Name" hint="Items are grouped under this heading, in the review and in the deliverable.">
        <input className="v2-input" value={name} autoFocus
               onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
      </Field>
    </Modal>
  );
};

export default AssessmentEditorScreen;
